import React, { useState, useEffect } from "react";
import { Rocket, Loader2, Coins, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

export const TEMPLATE_CATEGORIES = [
  ["platformer", "Platformer"], ["racing", "Racing"], ["shooter", "Shooter"],
  ["adventure", "Adventure"], ["puzzle", "Puzzle"], ["tower-defense", "Tower Defense"],
  ["survival", "Survival"], ["other", "Other"],
];

export function PublishTemplateDialog({ gameId, gameTitle, defaultCategory = "other", onPublished, trigger }) {
  const validCat = TEMPLATE_CATEGORIES.some(([k]) => k === defaultCategory) ? defaultCategory : "other";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(gameTitle || "");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(validCat);
  const [price, setPrice] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ipMatch, setIpMatch] = useState(null);

  useEffect(() => {
    const text = `${name} ${description}`.trim();
    if (text.length < 2) { setIpMatch(null); return; }
    const t = setTimeout(() => {
      api.get(`/rights/check-name?name=${encodeURIComponent(text)}`)
        .then(({ data }) => setIpMatch(data.protected ? data.match : null)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [name, description]);

  const submit = async () => {
    if (name.trim().length < 2) return toast.error("Give your template a name (2+ characters).");
    setBusy(true);
    try {
      const { data } = await api.post("/community-templates/publish", {
        game_id: gameId, name: name.trim(), description: description.trim(),
        category, credit_price: Math.max(0, parseInt(price, 10) || 0),
      });
      toast.success(`Published "${data.name}" to Community Templates! 🚀`);
      setOpen(false);
      onPublished && onPublished(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't publish template");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setName(gameTitle || ""); }}>
      <span onClick={() => setOpen(true)} data-testid="publish-template-trigger">
        {trigger || (
          <Button variant="outline" className="rounded-full" data-testid="publish-template-btn">
            <Rocket className="w-4 h-4" /> Publish as template
          </Button>
        )}
      </span>
      <DialogContent data-testid="publish-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-[hsl(var(--primary))]" /> Publish as a community template</DialogTitle>
          <DialogDescription>Share this whole project (config + all levels) so others can clone it with one click. Set a credit price to earn from each clone, or keep it free to grow your following.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Template name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Neon Kart Starter"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm" data-testid="pt-name" />
          </div>
          {ipMatch && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-500" data-testid="pt-ip-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>“{ipMatch}” resembles a protected brand/character. Use an original name & description you own the rights to before publishing.</span>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} rows={3} placeholder="What makes this template a great starting point?"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm resize-none" data-testid="pt-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="pt-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEMPLATE_CATEGORIES.map(([k, label]) => <SelectItem key={k} value={k} data-testid={`pt-category-${k}`}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Price (credits)</label>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2">
                <Coins className="w-4 h-4 text-amber-400" />
                <input type="number" min={0} max={5000} value={price} onChange={(e) => setPrice(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none" data-testid="pt-price" />
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{(parseInt(price, 10) || 0) === 0 ? "Free — drives followers & plays" : "You earn $ on every clone (minus platform fee)"}</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="pt-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !!ipMatch} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="pt-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Publish template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
